<template>
	<SectionCard>
		<template #title>
			Link
		</template>
		<template #title-action>
			<Dropdown
				v-model="selectedLanguage"
				custom-sort
				label="Language"
				searchable
				:items="filteredLanguageOptions"
				@change="updateEmbedCodeWithLanguage"
			/>
		</template>
		<template #body>
			<template v-if="enableOneClickUnsubscribe">
				<p>
					The following email header can be added to your outbound emails to enable One-Click Unsubscribe functionality. It supports both an API POST URI and a mailto fallback.
				</p>
				<p>
					Many modern email clients now prefer the API method, but including the mailto option is recommended when possible.
				</p>
				<p>
					<strong>
						How to use this header:
					</strong>
				</p>
				<ul>
					<li>
						Each Link field defined in the Fields step, as well as each Property in the Properties step, must be dynamically populated so the unsubscribe link corresponds to the correct data subject.
					</li>
					<li>
						If you do not wish to support the mailto method, you may omit that link.
					</li>
					<li>
						If you do include the mailto link, the subject should be URL encoded, as shown in the example below.
					</li>
				</ul>
			</template>
			<template v-else>
				<p>
					The following Link can be embedded within a Webpage Link or Email.  Select a Language to update the Link and set the required 'languagecode'.
				</p>
				<p>
					Each of the Link Fields specified in the Fields Step and the Properties in the Properties Step will need dynamically replacing accordingly so that the Link matches to the correct Data Subject.
				</p>
			</template>
			<v-row>
				<v-col
					cols="12"
					class="text-right"
				>
					<TextButton @click="copyToClipboard(true)">
						{{ enableOneClickUnsubscribe ? 'Copy Header' : 'Copy with tags' }}
					</TextButton>
					<TextButton
						v-if="!enableOneClickUnsubscribe"
						@click="copyToClipboard(false)"
					>
						Copy with URL only
					</TextButton>
					<TextButton
						v-if="shouldShowPreview"
						@click="openPreview(link)"
					>
						{{ enableOneClickUnsubscribe ? 'Preview Header' : 'Preview link' }}
					</TextButton>
				</v-col>
			</v-row>
			<Message
				v-if="enableOneClickUnsubscribe"
				type="info"
			>
				If you prefer to use your own domain in the mailto link rather than the default syrenis.io domain, you'll need to have an unsubscribe email address set up with us. Please contact your account manager to arrange this.
			</Message>
			<v-row dense>
				<v-col cols="12">
					<Prism>
						{{ displayCode }}
					</Prism>
				</v-col>
			</v-row>
		</template>
	</SectionCard>
</template>
<script>
import Prism from 'vue-prism-component'
import SectionCard from '../../../../../shared/components/section-card.vue'
import TextButton from '../../../../../shared/components/text-button.vue'
import Dropdown from '../../../../../shared/components/dropdown.vue'
import Message from '../../../../../shared/components/message.vue'
import { getLinkByID, encryptParameters } from '../../../../../shared/utils/api/admin-portal/links/links.js'
import { ACTION_LINK_TYPE, PUBLIC_PORTAL_LINK_TYPE, PREFERENCE_PAGE_LINK_TYPE } from '../links/link-types.js'
import { showSnackbar } from '../../../../../shared/state/snackbar.js'
import previewLinkMixin from '../../../../../shared/mixins/preview-link.js'
import { licenseId } from '../../../../../shared/state/config-keys.js'
import { getEnv } from '../../../../../shared/utils/runtime-settings.js'

export default {
	components: { TextButton, SectionCard, Prism, Dropdown, Message },
	mixins: [previewLinkMixin],
	props: {
		linkId: {
			type: Number,
			required: true
		},
		linkType: {
			type: Number,
			required: true
		},
		assignedLanguageItems: {
			type: Array,
			default: () => []
		},
		enableOneClickUnsubscribe: {
			type: Boolean,
			default: false
		}
	},
	setup: () => ({
		PUBLIC_PORTAL_LINK_TYPE,
		showSnackbar,
		licenseId
	}),
	data: () => ({
		embedCode: '',
		selectedLanguage: 0,
		selectedLanguageCode: 'default',
		link: {},
		lookupData: {}
	}),
	computed: {
		filteredLanguageOptions () {
			const noneOption = {
				text: 'None',
				value: 0
			}
			let filteredLanguages = []
			if (this.assignedLanguageItems) {
				filteredLanguages = this.assignedLanguageItems.filter(({ languageID }) => languageID !== 0)
				return [noneOption, ...filteredLanguages]
			} else {
				return []
			}
		},
		embedHref () {
			return new DOMParser()
				.parseFromString(this.embedCode, 'text/html')
				.getElementsByTagName('a')[0].href
		},
		shouldShowPreview () {
			return !(this.link.linkFields?.find(
				field => !field.previewValue || field.previewValue === ''
			) ?? null)
		},
		emailHeaderCode () {
			if (!this.enableOneClickUnsubscribe || !this.embedCode) return ''

			try {
				const url = new URL(this.embedHref)
				const params = url.searchParams

				// Build mailto domain from widget handler URL
				// Widget handler format: wscript-[region]-[env].cassiecloud.com (e.g., wscript-usw-dev.cassiecloud.com)
				// Mailto domain format: unsub.[region].[env].syrenis.io (or unsub.[region].syrenis.io for prod)
				const widgetHandlerUrl = getEnv('VUE_APP_WIDGETHANDLER') || ''
				let mailtoHost = 'unsub.syrenis.io' // fallback
				if (widgetHandlerUrl) {
					try {
						const widgetUrl = new URL(widgetHandlerUrl)
						// Extract region and env from subdomain (e.g., 'wscript-usw-dev' -> region='usw', env='dev')
						const subdomain = widgetUrl.hostname.split('.')[0] // 'wscript-usw-dev'
						const parts = subdomain.split('-') // ['wscript', 'usw', 'dev']
						if (parts.length >= 2) {
							const region = parts[1] // 'usw'
							const env = parts[2] // 'dev' or undefined for prod
							if (env && env !== 'prod') {
								mailtoHost = `unsub.${region}.${env}.syrenis.io`
							} else {
								mailtoHost = `unsub.${region}.syrenis.io`
							}
						}
					} catch {
						// Keep fallback
					}
				}

				// Build mailto subject using the same parameters as the One-Click URL
				// Extract all params except 'action' and 'LicenseID' which are not needed for mailto
				// Shorten 'sourcecode' to 'sc' and 'responsechannel' to 'rc' to reduce mailto subject length
				const paramKeyMap = {
					sourcecode: 'sc',
					responsechannel: 'rc'
				}
				const subjectParts = []
				params.forEach((value, key) => {
					if (key !== 'action' && key !== 'LicenseID' && key !== 'languagecode') {
						const mappedKey = paramKeyMap[key] || key
						subjectParts.push(mappedKey + '=' + value)
					}
				})
				const subjectParams = subjectParts.join('&')

				// Construct the email header format
				const mailtoAddress = `mailto:${this.licenseId}@${mailtoHost}?subject=${encodeURIComponent(subjectParams)}`
				const actionLinkUrl = this.embedHref

				return `List-Unsubscribe: <${mailtoAddress}>,\n  <${actionLinkUrl}>\nList-Unsubscribe-Post: List-Unsubscribe=One-Click`
			} catch {
				return 'Error generating email header'
			}
		},
		displayCode () {
			return this.enableOneClickUnsubscribe ? this.emailHeaderCode : this.embedCode
		}
	},
	async mounted () {
		await this.fetchLinkData()
		this.$root.$on('show-link-view-link-step', this.fetchLinkData)
	},
	beforeDestroy () {
		this.$root.$off('show-link-view-link-step', this.fetchLinkData)
	},
	methods: {
		async fetchLinkData () {
			// fetch the link again to get the embed code
			await getLinkByID(this.linkId).then(response => {
				this.lookupData = response.data.lookUpData
				const links = response.data
				switch (this.linkType) {
					case ACTION_LINK_TYPE:
						this.link = links.actionLinks[0]
						this.embedCode = links.actionLinks[0].actionLinkEmbedCode
						break
					case PUBLIC_PORTAL_LINK_TYPE:
						this.link = links.publicPortalLinks[0]
						this.embedCode = links.publicPortalLinks[0].publicPortalLinkEmbedCode
						break
					case PREFERENCE_PAGE_LINK_TYPE:
						this.link = links.preferencePageLinks[0]
						this.embedCode = links.preferencePageLinks[0].preferencePageLinkEmbedCode
						break
					default:
						this.embedCode = 'Error retrieving embed code'
				}
			})
		},
		copyToClipboard (includeTags) {
			if (this.enableOneClickUnsubscribe) {
				navigator.clipboard.writeText(this.emailHeaderCode)
					.catch(() => {
						showSnackbar({
							text: 'Error copying to clipboard',
							color: 'red'
						})
					})
			} else if (includeTags) {
				navigator.clipboard.writeText(this.embedCode)
			} else {
				const parser = new DOMParser()
				const link = parser.parseFromString(this.embedCode, 'text/html')
				const href = link.body.getElementsByTagName('a')[0].href
				navigator.clipboard.writeText(href)
					.catch(() => {
						showSnackbar({
							text: 'Error copying to clipboard',
							color: 'red'
						})
					})
			}
		},
		updateEmbedCodeWithLanguage (value) {
			const languageCode = this.assignedLanguageItems.find(({ value: languageID }) => languageID === value).languageCode
			this.embedCode = this.embedCode.replace(`languagecode=${this.selectedLanguageCode}`, `languagecode=${languageCode}`)
			this.selectedLanguageCode = languageCode
		},
		async openPreview (link) {
			const tempString = this.embedHref
			await this.previewLink(tempString, link)
		},
		async getEncryptedData (encryptedParams, link) {
			return await encryptParameters({
				encryptionKeyId: link.configurationOptions.encryption.encryptionKeyId,
				linkParams: encryptedParams
			})
		}
	}
}
</script>
